import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useWorkspace } from '@opallabs/hooks'
import { OpalApiResponseDocument, userGroups, users } from '../../../../v3'
import { globalQueryKeys } from '../../queryKeys'

type UsersResponse = OpalApiResponseDocument<'ReadUsersV3', 200>
type UserGroupsResponse = OpalApiResponseDocument<'ReadUserGroupsV3', 200>

const PAGE_LIMIT = 750

/**
 * Matches the `transformUser` label in `useUsersAndGroups`, which marks
 * deactivated users with a suffix.
 */
const formatUserLabel = (fullName: string, isActive: boolean): string =>
  isActive ? fullName : `${fullName} (Deactivated)`

type UsePeopleLabelMapOptions = {
  /** Also resolve user group ids. Only plan People fields can hold groups. */
  includeGroups?: boolean
}

/**
 * Returns an async function that builds a map of user (and optionally user
 * group) id to display name. People custom fields store only ids, so CSV
 * exports need this map to print names.
 *
 * The map is built on demand, so a page does not load the workspace's users
 * unless someone exports. It reads the `useUsersAndGroups` cache first and
 * fetches only what is missing.
 */
export const usePeopleLabelMap = ({
  includeGroups = false,
}: UsePeopleLabelMapOptions = {}): (() => Promise<Map<string, string>>) => {
  const queryClient = useQueryClient()
  const { uuid: workspaceUuid } = useWorkspace()

  return useCallback(async () => {
    const map = new Map<string, string>()

    const addUsers = (list: UsersResponse['data']) => {
      for (const user of list) {
        map.set(
          user.id,
          formatUserLabel(
            user.attributes.full_name,
            user.attributes.is_active ?? false
          )
        )
      }
    }

    // The key is shared with `useWorkspaceUsers`, which stores a flat document,
    // so only trust an infinite-query document with a known total.
    const cachedUsers = queryClient.getQueryData<{ pages?: UsersResponse[] }>(
      globalQueryKeys.workspaceUsers(workspaceUuid)
    )
    const cachedPages = Array.isArray(cachedUsers?.pages)
      ? cachedUsers.pages
      : []
    const cachedTotal = cachedPages[0]?.meta?.total
    const cachedCount = cachedPages.reduce(
      (sum, page) => sum + page.data.length,
      0
    )

    if (cachedTotal !== undefined && cachedCount >= cachedTotal) {
      cachedPages.forEach((page) => addUsers(page.data))
    } else {
      let offset = 0
      while (true) {
        const response = await users.readCollection({
          query: {
            filter: { workspace_id: [workspaceUuid] },
            page: { offset, limit: PAGE_LIMIT },
          },
        })
        addUsers(response.data)
        const total = response.meta?.total ?? 0
        if (total <= offset + PAGE_LIMIT) break
        offset += PAGE_LIMIT
      }
    }

    if (!includeGroups) return map

    const groupsFilter = { members: { workspace_id: [workspaceUuid] } }
    const groupsResponse =
      queryClient.getQueryData<UserGroupsResponse>(
        globalQueryKeys.groups(groupsFilter, ['asset'])
      ) ??
      (await userGroups.readCollection({
        params: { query: { filter: groupsFilter } },
      }))
    for (const group of groupsResponse.data ?? []) {
      map.set(group.id, group.attributes.name)
    }

    return map
  }, [includeGroups, queryClient, workspaceUuid])
}
